/**
 * Frontend permission helpers.
 *
 * UI-affordance only — real authorization is enforced backend in
 * ChronosLambdas (resolve_permissions + per-endpoint can_validate /
 * programs gates). These helpers decide which nav items to render so
 * calculator-only users (the ChronosGeneral Federate group, P449942577)
 * don't see fund-request / reporting tabs they would be 403'd on anyway.
 */

/**
 * Returns true when the user is in ONLY the 'general' group — the
 * calculator-only ChronosGeneral tier. A user in any program/admin group
 * (even alongside 'general') gets the full nav.
 *
 * Derived from groups WITHOUT enumerating validation groups: a new
 * program onboarding adds a role:<program> to chronos_config and a
 * Federate group; the frontend doesn't need to learn about it. Anyone
 * not exclusively in 'general' is treated as a full-access user.
 *
 * @param {{ groups?: string[] } | null | undefined} user
 * @returns {boolean}
 */
export function isCalculatorOnlyUser(user) {
  const groups = user?.groups;
  if (!Array.isArray(groups) || groups.length === 0) {
    return false;
  }
  return groups.every(g => g === 'general');
}

/**
 * The landing href a user should be sent to after auth (and from the
 * logo / "Chronos" header click). Calculator-only users land on the
 * calculator; everyone else lands on the Fund Requests dashboard.
 *
 * Pre-fix, every entry path (post-login redirect in Auth.js, logo
 * click in App.js, the Switch catch-all `<Redirect to="/dashboard">`)
 * sent calc-only users to FundingRequestTracker — a page the backend
 * 403s every API call on. The /dashboard route is the single guarded
 * redirect site (see App.js); this helper centralizes the landing
 * decision so any new entry-point picks up the rule for free.
 *
 * @param {{ groups?: string[] } | null | undefined} user
 * @returns {string}
 */
export function defaultLandingHref(user) {
  return isCalculatorOnlyUser(user) ? '/calculator-check' : '/dashboard';
}
